<template>
  <DefaultLayout>
    <div class="flex flex-col px-4 py-4">
      <PageHeader use-back title="Módulo Gerencial" subtitle="Supervisório gerencial personalizável por empresa.">
        <template #actions>
          <div class="flex flex-wrap items-center gap-2">
            <PeriodRangeDialog v-if="usesPeriod" :period="store.period" @change-date="store.setPeriod" />
            <button
              v-if="activeTab === 'comparacao' && store.activeArea"
              type="button"
              class="border border-primary px-3 py-2 text-sm font-medium text-primary hover:bg-primary/10"
              title="Adiciona a área selecionada com o período atual à comparação"
              @click="addToComparison">
              + Comparar ({{ store.activeArea.name }})
            </button>
            <button
              v-if="usesPeriod"
              type="button"
              class="border border-stroke px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              :disabled="store.loadingIndicators"
              title="Recalcular ignorando o cache"
              @click="store.refreshIndicators">
              ↻ Atualizar
            </button>
            <button
              type="button"
              class="border border-primary bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
              :disabled="store.generatingReport"
              title="Gera o PDF do relatório gerencial com os dados já calculados"
              @click="store.generateReport">
              {{ store.generatingReport ? 'Gerando…' : '📄 Gerar relatório' }}
            </button>
          </div>
        </template>
      </PageHeader>

      <!-- Abas -->
      <div class="mb-4 flex gap-1 border-b border-stroke">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          class="-mb-px border-b-2 px-4 py-2 text-sm font-medium transition"
          :class="activeTab === tab.key ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-700'"
          @click="activeTab = tab.key">
          {{ tab.label }}
        </button>
      </div>

      <p v-if="usesPeriod" class="mb-3 text-sm text-slate-500">
        Período analisado: <span class="font-medium text-slate-700">{{ periodLabel }}</span>
      </p>

      <Loader v-if="store.loadingTree && !store.roots.length" />

      <!-- Aba: Painel — organizado em seções: destaque, visão geral, análise por área, previsão -->
      <div v-else-if="activeTab === 'painel'" class="flex flex-col gap-6">
        <FavoriteOverview v-if="store.showFavoriteOverview" />

        <ManagementSection
          title="Visão geral das linhas"
          subtitle="Indicadores de todas as linhas no período. Clique em uma linha para ver as paradas por tipo.">
          <AreasOverviewTable />
        </ManagementSection>

        <ManagementSection
          title="Análise por área"
          subtitle="Selecione uma área ou sensor para ver os indicadores e o gráfico diário de paradas.">
          <ManagementColumns
            @add-area="onAddArea"
            @add-sensor="onAddSensor"
            @edit-area="onEditArea"
            @remove-area="onRemoveArea"
            @edit-sensor="onEditSensor"
            @remove-sensor="onRemoveSensor" />
          <IndicatorPanel />
          <DailyRuntimeChart />
        </ManagementSection>

        <ManagementSection
          v-if="store.roots.length"
          title="Previsão de consumo"
          subtitle="Previsto × realizado de energia da área favorita no ano vigente.">
          <ForecastVsActualPanel />
        </ManagementSection>
      </div>

      <!-- Aba: Visão consolidada (1 linha por área/linha de produção) -->
      <div v-else-if="activeTab === 'consolidada'" class="flex flex-col gap-4">
        <ManagementColumns
          @add-area="onAddArea"
          @add-sensor="onAddSensor"
          @edit-area="onEditArea"
          @remove-area="onRemoveArea"
          @edit-sensor="onEditSensor"
          @remove-sensor="onRemoveSensor" />
        <ConsolidatedAreasTab />
      </div>

      <!-- Aba: Comparação -->
      <div v-else-if="activeTab === 'comparacao'" class="flex flex-col gap-4">
        <ManagementColumns
          @add-area="onAddArea"
          @add-sensor="onAddSensor"
          @edit-area="onEditArea"
          @remove-area="onRemoveArea"
          @edit-sensor="onEditSensor"
          @remove-sensor="onRemoveSensor" />
        <ComparisonView />
      </div>

      <!-- Aba: Manutenções -->
      <ManagementMaintenanceTab v-else-if="activeTab === 'manutencoes'" />

      <!-- Aba: Configurações (Módulo + Tarifas + Previsto + Metas de alarme) -->
      <div v-else-if="activeTab === 'configuracoes'" class="flex flex-col gap-8">
        <ModuleSettingsSection />

        <TariffsSection />

        <AlarmTargetsSection />

        <ForecastEditor />
      </div>

      <!-- Aba: Consolidado mensal -->
      <ConsolidatedTab v-else />
    </div>

    <AddAreaModal
      :is-open="areaModal.open"
      :mode="areaModal.mode"
      :initial-name="areaModal.initialName"
      :parent-name="areaModal.parentName"
      @cancel="areaModal.open = false"
      @save="saveArea" />

    <AddSensorToAreaModal
      :is-open="sensorModal.open"
      :area="sensorModal.area"
      :sensors="store.companySensors"
      :editing="sensorModal.editing"
      @cancel="sensorModal.open = false"
      @save="saveSensor"
      @save-changes="saveSensorChanges" />

    <ConfirmationModal
      :is-open="deleteState.open"
      cancel-text="Cancelar"
      confirmation-text="Excluir"
      @cancel="deleteState.open = false"
      @add="executeDelete">
      <template #content>
        <div class="space-y-2">
          <h3 class="text-lg font-semibold text-slate-900">
            {{ deleteState.kind === 'area' ? 'Excluir área?' : 'Remover sensor da área?' }}
          </h3>
          <p class="text-sm text-slate-600">
            <strong>{{ deleteState.label }}</strong>
            {{ deleteState.kind === 'area' ? 'e tudo abaixo dela serão removidos.' : 'será desvinculado desta área.' }}
          </p>
        </div>
      </template>
    </ConfirmationModal>
  </DefaultLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import ConfirmationModal from '@/components/Globals/ConfirmationModal.vue'
import PeriodRangeDialog from '@/components/Globals/Inputs/PeriodRangeDialog.vue'
import Loader from '@/components/Globals/Feedbacks/Loader.vue'
import PageHeader from '@/components/Globals/PageHeader.vue'
import AddAreaModal from '@/components/Management/Columns/AddAreaModal.vue'
import AddSensorToAreaModal from '@/components/Management/Columns/AddSensorToAreaModal.vue'
import ManagementColumns from '@/components/Management/Columns/ManagementColumns.vue'
import ConsolidatedTab from '@/components/Management/Consolidated/ConsolidatedTab.vue'
import ComparisonView from '@/components/Management/Indicators/ComparisonView.vue'
import ForecastEditor from '@/components/Management/Forecast/ForecastEditor.vue'
import ForecastVsActualPanel from '@/components/Management/Forecast/ForecastVsActualPanel.vue'
import TariffsSection from '@/components/Management/Tariffs/TariffsSection.vue'
import AlarmTargetsSection from '@/components/Management/AlarmTargets/AlarmTargetsSection.vue'
import ManagementMaintenanceTab from '@/components/Management/Maintenance/ManagementMaintenanceTab.vue'
import FavoriteOverview from '@/components/Management/Indicators/FavoriteOverview.vue'
import IndicatorPanel from '@/components/Management/Indicators/IndicatorPanel.vue'
import DailyRuntimeChart from '@/components/Management/Indicators/DailyRuntimeChart.vue'
import ConsolidatedAreasTab from '@/components/Management/Consolidated/ConsolidatedAreasTab.vue'
import ModuleSettingsSection from '@/components/Management/Settings/ModuleSettingsSection.vue'
import AreasOverviewTable from '@/components/Management/Overview/AreasOverviewTable.vue'
import ManagementSection from '@/components/Management/ManagementSection.vue'
import type { IAreaSensor, IManagementArea } from '@/interfaces/entities/management'
import { useErrorStore } from '@/store/error'
import { useManagementStore } from '@/store/management'
import DefaultLayout from '@/layouts/DefaultLayout.vue'

const store = useManagementStore()

type TabKey = 'painel' | 'consolidada' | 'comparacao' | 'consolidado' | 'manutencoes' | 'configuracoes'
const tabs: { key: TabKey; label: string }[] = [
  { key: 'painel', label: 'Painel' },
  { key: 'consolidada', label: 'Visão consolidada' },
  { key: 'comparacao', label: 'Comparação' },
  { key: 'consolidado', label: 'Consolidado mensal' },
  { key: 'manutencoes', label: 'Manutenções' },
  { key: 'configuracoes', label: 'Configurações' },
]
const activeTab = ref<TabKey>('painel')

// Abas que usam o período (range de datas); consolidado e previsto usam mês/ano próprios.
const usesPeriod = computed(
  () => activeTab.value === 'painel' || activeTab.value === 'comparacao' || activeTab.value === 'manutencoes',
)

// Intervalo analisado, exibido no datepicker (placeholder) e no rótulo da toolbar.
const periodLabel = computed(() => {
  if (store.period.length < 2) return 'Selecione o período'
  const fmt = (date: Date) =>
    new Date(date).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  return `${fmt(store.period[0])} até ${fmt(store.period[1])}`
})

const areaModal = reactive<{
  open: boolean
  mode: 'create' | 'rename'
  parentId: number | null
  areaId: number | null
  initialName: string
  parentName: string | null
}>({ open: false, mode: 'create', parentId: null, areaId: null, initialName: '', parentName: null })

const sensorModal = reactive<{ open: boolean; area: IManagementArea | null; editing: IAreaSensor | null }>({
  open: false,
  area: null,
  editing: null,
})

const deleteState = reactive<{
  open: boolean
  kind: 'area' | 'sensor'
  label: string
  area: IManagementArea | null
  areaSensor: IAreaSensor | null
}>({ open: false, kind: 'area', label: '', area: null, areaSensor: null })

function onAddArea(parentId: number | null) {
  areaModal.mode = 'create'
  areaModal.parentId = parentId
  areaModal.areaId = null
  areaModal.initialName = ''
  areaModal.parentName = parentId ? store.nodesById[parentId]?.name ?? null : null
  areaModal.open = true
}

function onEditArea(area: IManagementArea) {
  areaModal.mode = 'rename'
  areaModal.areaId = area.id
  areaModal.initialName = area.name
  areaModal.parentName = null
  areaModal.open = true
}

async function saveArea(name: string) {
  try {
    if (areaModal.mode === 'create') {
      await store.createArea(areaModal.parentId, name)
    } else if (areaModal.areaId) {
      await store.renameArea(areaModal.areaId, name)
    }
    areaModal.open = false
  } catch {
    useErrorStore().setErrors(['Não foi possível salvar a área.'])
  }
}

function onAddSensor(area: IManagementArea) {
  sensorModal.area = area
  sensorModal.editing = null
  sensorModal.open = true
}

function onEditSensor({ area, areaSensor }: { area: IManagementArea; areaSensor: IAreaSensor }) {
  sensorModal.area = area
  sensorModal.editing = areaSensor
  sensorModal.open = true
}

async function saveSensor({ sensorId, indicators }: { sensorId: number; indicators: string[] }) {
  if (!sensorModal.area) return
  try {
    if (sensorModal.editing) {
      await store.updateSensorIndicators(sensorModal.area.id, sensorModal.editing.id, indicators)
    } else {
      await store.attachSensor(sensorModal.area.id, sensorId, indicators)
    }
    sensorModal.open = false
  } catch (e: any) {
    useErrorStore().setErrors(e?.response?.data?.errors ?? ['Não foi possível salvar o sensor.'])
  }
}

async function saveSensorChanges(changes: { add: { sensorId: number; indicators: string[] }[]; remove: number[] }) {
  if (!sensorModal.area) return
  try {
    await store.syncAreaSensors(sensorModal.area.id, changes)
    sensorModal.open = false
  } catch (e: any) {
    useErrorStore().setErrors(e?.response?.data?.errors ?? ['Não foi possível atualizar os sensores.'])
  }
}

function onRemoveArea(area: IManagementArea) {
  deleteState.kind = 'area'
  deleteState.area = area
  deleteState.areaSensor = null
  deleteState.label = area.name
  deleteState.open = true
}

function onRemoveSensor({ area, areaSensor }: { area: IManagementArea; areaSensor: IAreaSensor }) {
  deleteState.kind = 'sensor'
  deleteState.area = area
  deleteState.areaSensor = areaSensor
  deleteState.label = areaSensor.sensor_name
  deleteState.open = true
}

async function executeDelete() {
  try {
    if (deleteState.kind === 'area' && deleteState.area) {
      await store.deleteArea(deleteState.area.id)
    } else if (deleteState.kind === 'sensor' && deleteState.area && deleteState.areaSensor) {
      await store.detachSensor(deleteState.area.id, deleteState.areaSensor.id)
    }
  } catch {
    useErrorStore().setErrors(['Não foi possível concluir a remoção.'])
  } finally {
    deleteState.open = false
  }
}

function addToComparison() {
  if (store.activeArea) {
    store.addComparisonEntry(store.activeArea.id, store.period)
    activeTab.value = 'comparacao'
  }
}

onMounted(() => {
  store.init()
})
</script>
